import useAuth from 'hooks/useAuth';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import { isListedInDesign } from 'pages/admin/adminItems';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import MainRoutes from 'routes/MainRoutes';
import { FeatureFlag } from 'types/features';

export { filterRoutes } from './routeFilter';

export interface SearchableRoute {
    path: string;
    breadcrumbPaths: string[];
    labelId: string;
    label: string;
    isDynamic: boolean;
    permissions?: string[];
    requiredFeature?: FeatureFlag;
}

interface RouteObject {
    path?: string;
    breadcrumbs?: { path: string }[];
    searchableName?: string;
    permissions?: string[];
    requiredFeature?: FeatureFlag;
    children?: RouteObject[];
}

function extractSearchableRoutes(routes: RouteObject): SearchableRoute[] {
    const result: SearchableRoute[] = [];

    function traverse(route: RouteObject) {
        if (route.path && route.searchableName) {
            const isDynamic = route.path.includes(':');
            result.push({
                path: route.path,
                breadcrumbPaths: route.breadcrumbs?.map(({ path }) => path) ?? [],
                labelId: route.searchableName,
                label: '', // Will be filled with translated label
                isDynamic,
                permissions: route.permissions,
                requiredFeature: route.requiredFeature,
            });
        }

        if (route.children) {
            route.children.forEach(traverse);
        }
    }

    traverse(routes);
    return result;
}

export function useSearchableRoutes(): SearchableRoute[] {
    const intl = useIntl();
    const { user } = useAuth();
    const isNew = useIsNewDesign();

    const routes = useMemo(() => {
        const extracted = extractSearchableRoutes(MainRoutes as RouteObject);

        // Add translated labels
        return extracted.map((route) => ({
            ...route,
            label: intl.formatMessage({ id: route.labelId, defaultMessage: route.labelId }),
        }));
    }, [intl]);

    // Filter routes based on the design, user permissions and feature flags
    const accessibleRoutes = useMemo(() => {
        return routes.filter((route) => {
            if (!isListedInDesign(route.path, isNew)) {
                return false;
            }
            // Check feature flag
            if (route.requiredFeature && !user?.enabledFeatures?.includes(route.requiredFeature)) {
                return false;
            }
            // If no permissions required, route is accessible
            if (!route.permissions || route.permissions.length === 0) {
                return true;
            }
            // Check if user has at least one of the required permissions
            return user?.permissions?.some((p) => route.permissions!.includes(p.code)) ?? false;
        });
    }, [routes, user?.permissions, user?.enabledFeatures, isNew]);

    return accessibleRoutes;
}
